import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Building2, Tag, Calendar, X } from "lucide-react";

import api from "../../lib/api";
import AdminLayout from "../../components/admin/AdminLayout";
import ContactsTable from "../../components/admin/ContactsTable";

const isReadValue = (c) =>
  c.is_read === true || c.is_read === 1 || c.is_read === "1";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminContact = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  // The contact currently being viewed in the detail modal.
  const [viewContact, setViewContact] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        // Auth token is attached automatically by the api interceptor.
        const { data } = await api.get("/contact/us-get");
        // Be tolerant of different response shapes (array, {data}, {contacts}).
        const list = Array.isArray(data)
          ? data
          : data?.data || data?.contacts || [];
        setContacts(list);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to load contacts"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  // Open the detail modal and fetch the full contact from the API.
  const handleView = async (contact) => {
    // Show what we already have immediately, then refresh from the API.
    setViewContact(contact);
    setViewLoading(true);

    // Opening a contact marks it as read — flip the toggle automatically.
    const markedRead = !isReadValue(contact);
    if (markedRead) {
      setContacts((prev) =>
        prev.map((c) => (c.id === contact.id ? { ...c, is_read: 1 } : c))
      );
      setViewContact((prev) =>
        prev && prev.id === contact.id ? { ...prev, is_read: 1 } : prev
      );
      api
        .post(`/contact/us/${contact.id}/is-read`, { is_read: 1 })
        .catch(() => {
          // Revert if the backend rejects it.
          setContacts((prev) =>
            prev.map((c) =>
              c.id === contact.id ? { ...c, is_read: contact.is_read } : c
            )
          );
        });
    }

    try {
      const { data } = await api.get(`/contact/us-get/${contact.id}`);
      const detail = data?.data || data?.contact || data;
      if (detail && typeof detail === "object") {
        // If we just marked it read, keep it read — the detail GET can return
        // a stale is_read before the mark-read request lands.
        const merged = markedRead ? { ...detail, is_read: 1 } : detail;
        setViewContact(merged);
        if ("is_read" in merged) {
          setContacts((prev) =>
            prev.map((c) =>
              c.id === merged.id ? { ...c, is_read: merged.is_read } : c
            )
          );
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load contact details"
      );
    } finally {
      setViewLoading(false);
    }
  };

  // Toggle is_read. Update optimistically, roll back on error.
  const handleToggleRead = async (contact) => {
    const original = contact.is_read;
    const newStatus = isReadValue(contact) ? 0 : 1;

    setContacts((prev) =>
      prev.map((c) =>
        c.id === contact.id ? { ...c, is_read: newStatus } : c
      )
    );

    try {
      await api.post(`/contact/us/${contact.id}/is-read`, {
        is_read: newStatus,
      });
      toast.success(newStatus ? "Marked as read" : "Marked as unread");
    } catch (error) {
      // Revert on failure.
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contact.id ? { ...c, is_read: original } : c
        )
      );
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  return (
    <AdminLayout title="Contact Us">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage contact form submissions.
        </p>
      </div>

      <ContactsTable
        contacts={contacts}
        loading={loading}
        onToggleRead={handleToggleRead}
        onView={handleView}
      />

      {/* View mail modal */}
      {viewContact && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
          onClick={() => setViewContact(null)}
        >
          <div
            className="relative my-10 w-full max-w-lg rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewContact(null)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="border-b px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#b32126]/10 font-semibold text-[#b32126]">
                  {(viewContact.full_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-bold text-gray-900">
                    {viewContact.full_name || "—"}
                  </h3>
                  {viewContact.category && (
                    <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#b32126]/10 px-2 py-0.5 text-xs font-medium text-[#b32126]">
                      <Tag className="h-3 w-3" />
                      {viewContact.category}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="space-y-4 px-6 py-5">
              {viewLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading latest details…
                </div>
              )}

              <div className="grid gap-3 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                  {viewContact.email ? (
                    <a
                      href={`mailto:${viewContact.email}`}
                      className="hover:text-[#b32126]"
                    >
                      {viewContact.email}
                    </a>
                  ) : (
                    <span className="text-gray-400">No email</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="h-4 w-4 shrink-0 text-gray-400" />
                  {viewContact.phone_number ? (
                    <a
                      href={`tel:${viewContact.phone_number}`}
                      className="hover:text-[#b32126]"
                    >
                      {viewContact.phone_number}
                    </a>
                  ) : (
                    <span className="text-gray-400">No phone</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                  {viewContact.company_name || (
                    <span className="text-gray-400">No company</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                  {formatDate(viewContact.created_at)}
                </div>
              </div>

              {/* Message */}
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Message
                </p>
                <div className="rounded-md border border-gray-100 bg-gray-50 p-4 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">
                  {viewContact.description || "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContact;
